import { FlashSale, FlashSaleProduct } from '@/types/flash-sale'

interface FormattedFlashSales {
  current: FlashSale[]
  next: FlashSale[]
  countDownTime: Date | null
}

// Format a single product for flash sale
const formatFlashSaleProduct = (
  product: FlashSaleProduct,
  flashSaleValue: string
): FlashSaleProduct => {
  // Calculate prices and discount
  const regular_price = product.regular_price || 0

  const compare_price = product.compare_price || regular_price
  const percent =
    regular_price < compare_price
      ? 100 - Math.round((regular_price / compare_price) * 100)
      : 0
  const sale_percent = parseInt(flashSaleValue) || percent || 0

  // Format the product according to FlashSaleProduct interface
  return {
    // ProductFormatted properties
    title: product.product_title || '',
    href: product.product_seo_alias?.includes('product')
      ? product.product_seo_alias
      : `/product/${product.product_seo_alias}`,
    is_setdo: product.is_setdo || false,
    id: product.product_id || '',
    regular_price,
    compare_price,
    sale_percent,
    default_thumbnail: product?.product_images || '',
    default_thumbnail_hover: product?.product_images || '',
    active: true,
    mapped_variants: product.mapped_variants || {},
    tags: product.tags || [],
    is_free_shipping: product.is_free_shipping || false,
    is_quick_add: product.is_quick_add || false,
    note: product.note || '',

    // FlashSaleProduct specific properties
    product_id: product.product_id || '',
    product_title: product.product_title || '',
    product_seo_alias: product.product_seo_alias || '',
    product_images: product.product_images || '',

    //random giữa hot và low_stock
    product_status: Math.random() > 0.5 ? 'hot' : 'low_stock',
  }
}

// Format all products in a flash sale
const formatFlashSaleProducts = (flashSale: FlashSale): FlashSale => {
  if (!flashSale.products || !Array.isArray(flashSale.products)) {
    return flashSale
  }

  // Format each product in the flash sale
  const formattedProducts = flashSale.products.map((product) =>
    formatFlashSaleProduct(product, flashSale.flash_sale_value)
  )

  // Return the flash sale with formatted products
  return {
    ...flashSale,
    products: formattedProducts,
  }
}

const reformatFlashSale = (
  rawData: FlashSale[],
  programIds?: string[]
): FormattedFlashSales => {
  // Lọc theo chương trình được chọn trước khi giới hạn số lượng hiển thị
  const data = programIds
    ? (rawData || []).filter((item) => programIds.includes(item._id))
    : rawData

  if (!data || data.length === 0) {
    return {
      current: [],
      next: [],
      countDownTime: null,
    }
  }

  const now = new Date()
  const sortedFlashSales = [...data].sort((a, b) => {
    return new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
  })

  // Filter and format current flash sales
  const current = sortedFlashSales
    .filter((flashSale) => {
      const startTime = new Date(flashSale.starts_at)
      const endTime = new Date(flashSale.ends_at)
      return startTime <= now && now <= endTime
    })
    .map((flashSale) => formatFlashSaleProducts(flashSale))

  // Filter and format upcoming flash sales
  const next = sortedFlashSales
    .filter((flashSale) => {
      const startTime = new Date(flashSale.starts_at)
      return startTime > now
    })
    .map((flashSale) => formatFlashSaleProducts(flashSale))

  // Tính thời gian còn lại cho flash sale hiện tại, hoặc thời gian đến flash sale tiếp theo
  let countDownTime = null

  if (current.length > 0) {
    // Nếu có flash sale đang diễn ra, lấy thời gian kết thúc của flash sale đầu tiên
    countDownTime = new Date(current[0].ends_at)
  } else if (next.length > 0) {
    // Nếu không có flash sale đang diễn ra, lấy thời gian bắt đầu của flash sale sắp diễn ra đầu tiên
    countDownTime = new Date(next[0].starts_at)
  }

  // Tổng chỉ lấy 2 chương trình: có 1 đang diễn ra thì 1 current + 1 next, không thì 2 next
  const TOTAL_PROGRAMS = 2
  const currentCount = Math.min(current.length, 1)
  const nextCount = TOTAL_PROGRAMS - currentCount

  return {
    current: current.slice(0, currentCount),
    next: next.slice(0, nextCount),
    countDownTime,
  }
}

export const FlashSaleFormatter = {
  reformatFlashSale,
  formatFlashSaleProduct,
}
